/**
 * Sistema de caché de imágenes en memoria
 * Evita recargas innecesarias de imágenes
 */

const imageCache = new Map();
const preloadedImages = new Set();

/**
 * Precargar imagen en caché del navegador
 * @param {string} url - URL de la imagen
 * @returns {Promise<void>}
 */
export const preloadImage = (url) => {
  if (!url || preloadedImages.has(url)) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const img = new Image();
    
    img.onload = () => {
      preloadedImages.add(url);
      imageCache.set(url, true);
      resolve();
    };
    
    img.onerror = () => {
      reject(new Error(`Failed to preload image: ${url}`));
    };
    
    // Iniciar carga
    img.src = url;
  });
};

/**
 * Precargar múltiples imágenes
 * @param {string[]} urls - Array de URLs
 * @returns {Promise<void[]>}
 */
export const preloadImages = (urls) => {
  return Promise.all(urls.filter(Boolean).map(preloadImage));
};

/**
 * Verificar si imagen está en caché
 * @param {string} url - URL de la imagen
 * @returns {boolean}
 */
export const isImageCached = (url) => {
  return imageCache.has(url) || preloadedImages.has(url);
};

/**
 * Limpiar caché de imágenes
 */
export const clearImageCache = () => {
  imageCache.clear();
  preloadedImages.clear();
};

/**
 * Obtener estadísticas del caché
 * @returns {object}
 */
export const getCacheStats = () => {
  return {
    size: imageCache.size,
    preloaded: preloadedImages.size,
    urls: Array.from(preloadedImages)
  };
};
